/**
 * The LV masthead — one full-width bar above the assistant/surfaces split,
 * implementing the approved Experience Header design
 * (docs/design-references/tc-learnerview-headers.png, 2026-10-08 rulings +
 * the 2026-10-09 visual-audit corrections). Three regions (Isobel, 2026-10-11):
 * LEFT the customer's brand mark (brand.logoUrl; absent = the deployment's
 * name as text, the brand model's own fallback; deliberately NOT a link);
 * CENTRE Home and Explore, centred in the space between the logo region and
 * the utility controls while space permits; RIGHT, in the design's order,
 * Appearance, Mailbox, Identity, Sign out — 44px-centred items with a thin
 * vertical separator at ONLY the Mailbox↔Identity and Identity↔Sign out
 * junctions, centred on the cross axis at every width.
 *
 *   HOME       activates tab zero (the markdown mini-site, always open);
 *              the active destination carries the design's 3px rounded
 *              underline beneath the label + aria-current.
 *   EXPLORE    the approved accordion panel (the 2026-09-24 Hatchery mock):
 *              an "Explore" title, a Home row, then Home's site menu rendered
 *              by the CANONICAL accordion renderer (vendored
 *              renderNavAccordionItems — interactive folder rows, guide
 *              lines, the current page's pill and its ancestors auto-expanded,
 *              publish dates kept per Douglas 2026-09-20), a divider, then
 *              the deployment's enabled surfaces and nav folders in the same
 *              accordion language, painted from the same exported style
 *              fragments. A menuOp surface's row is an accordion PARENT —
 *              the whole row expands inline to its scoped subpages (e.g.
 *              each connection group) and never navigates (Isobel
 *              2026-10-09; expandability rides the manifest's menuOp, never
 *              the fetched items). While the panel is open the bar
 *              button carries the same underline as the active destination.
 *   APPEARANCE one sun/moon TOGGLE over the existing brand.nightMode policy —
 *              one click flips Light/Dark (no menu), tooltip "Switch to dark/
 *              light mode"; absent entirely when the policy is 'off'.
 *   INBOX      signed in + nav.inbox.iconEnabled: the mailbox icon with the
 *              unread badge (classic login_details.xsl parity; hidden by
 *              default exactly like classic). Absent in the public state.
 *   IDENTITY   signed in: avatar (the learner's classic profile photo when
 *              one exists — bearer-fetched once per tab — with INITIALS as
 *              the fallback) + the display name, which drops to the bare
 *              avatar when the bar is collapsed. DISPLAY-ONLY: the account
 *              dropdown is retired (Isobel 2026-10-09) — its entries
 *              duplicated Explore's surface rows and the header's own
 *              mailbox icon, and Sign out keeps its dedicated bar icon.
 *              Public: the gate's existing Sign in button.
 *   SIGN OUT   a dedicated bar icon (learner only) invoking the shell's
 *              existing onSignOut.
 *
 * COLLAPSE (the design's responsive rule): the bar measures its OWN content —
 * never a device breakpoint. When the full content no longer fits, Home and
 * Explore fold into a hamburger that opens the right-side navigation DRAWER
 * (the same destinations and callbacks, in the same accordion language:
 * inset rounded selected pills, guide lines, expandable rows); the identity
 * name drops to initials at the same moment. Expansion needs the full
 * content to fit again with hysteresis, so the state can't oscillate.
 *
 * TYPOGRAPHY (v3): navigation text is CONTENT, not fixed chrome — bar labels,
 * the Explore panel's title and rows, and the drawer's rows and section
 * labels all follow the configured base font size (Isobel, 2026-10-09).
 * Variant-driven text (body2/caption rows) scales through MUI's own
 * coefficient; the design's fixed-px labels ride navScaledPx so they grow in
 * step. Rows are min-height and the collapse is content-measured, so larger
 * text reflows instead of clipping.
 *
 * PUBLIC STATE (no session): the LV stays sign-in-gated. The bar renders the
 * same destinations LOCKED and every locked click runs the gate's existing
 * sign-in flow — nothing becomes publicly reachable, and Home's pages are
 * never fetched anonymously (the /home API requires the learner session).
 */
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import {
  Avatar, Badge, Box, Button, CircularProgress, Collapse, Divider, Drawer, IconButton, List,
  ListItemButton, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Stack, Tooltip,
  Typography, type Theme,
} from '@mui/material'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import DescriptionIcon from '@mui/icons-material/Description'
import FolderIcon from '@mui/icons-material/Folder'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import HomeIcon from '@mui/icons-material/Home'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import LockIcon from '@mui/icons-material/Lock'
import LoginIcon from '@mui/icons-material/Login'
import LogoutIcon from '@mui/icons-material/Logout'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import MenuIcon from '@mui/icons-material/Menu'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import FlipIcon from '@mui/icons-material/Flip'
import { type LvClientConfig, type LvFolderEntry } from './lvConfig'
import LvLogo from './LvLogo'
import { selfPhotoUrl } from './avatar'
import type { LvSurfaceDef } from './registry'
import { SurfaceGlyph } from './icons'
import {
  navAccordionAncestorSx, navAccordionContainsCurrent, navAccordionGuideSx, navAccordionIconSx,
  navAccordionInitialKeys, navAccordionKey, navAccordionRowSx, navAccordionSelectedSx,
  renderNavAccordionItems, type NavAccordionState,
} from '../home/vendor/home/navMenuItems'
import type { HomeNavLink } from '../home/vendor/home/types'
import type { SurfaceMenuItem } from './surfaceMenus'

export interface LvHeaderExplore {
  /** Home's site menu (empty in the public state — Home is session-gated). */
  homeNav: HomeNavLink[]
  /** The page Home is currently showing, for the menu's self-mark. */
  currentFile?: string
  /** The deployment's enabled surfaces, resolved (label/icon/href applied). */
  surfaces: LvSurfaceDef[]
  /** menuOp expansion items per surface (absent in the public state — the
   *  locked menu never fetches, so no row ever expands there). These only
   *  FILL an expanded row: expandability itself rides the manifest's menuOp,
   *  so an unresolved or failed fetch leaves the row expandable, showing its
   *  loading/empty state (Isobel 2026-10-09). */
  menus?: Record<string, SurfaceMenuItem[]>
  /** Public state: every destination is locked and routes to sign-in. */
  locked: boolean
  onHomeNav: (n: HomeNavLink) => void
  /** label/params ride an expansion pick: the tab takes the item's name and
   *  the surface is scoped by `params.menu`. A plain row passes neither. */
  onSurface: (id: string, label?: string, params?: Record<string, string>) => void
  /** A locked destination was clicked — the parent runs the sign-in flow. */
  onLocked: () => void
}

export interface LvHeaderAccount {
  kind: 'public' | 'learner'
  /** Learner identity (token claims / /me). The avatar shows the classic
   *  profile photo (fetched from /auth/me/photo) with INITIALS as the
   *  fallback — there is no menu behind it (Isobel 2026-10-09). */
  name?: string
  email?: string
  /** Review/fixture override: skip the photo fetch and use this URL. */
  avatarUrl?: string
  /** Public only: the sign-in round-trip has started. */
  signingIn?: boolean
  onSignIn?: () => void
  onSignOut?: () => void
}

export interface LvHeaderAppearance {
  /** brand.nightMode policy — false renders no control at all. */
  allowed: boolean
  mode: 'light' | 'dark'
  /** The toggle invokes this with the opposite of the current mode. */
  onChoose: (m: 'light' | 'dark') => void
}

export interface LvHeaderInbox {
  /** Unread count on the badge (0 = bare icon). */
  unread: number
  onOpen: () => void
}

/** Initials for the avatar chip — the session carries only name/email. */
function initials(name: string, email: string): string {
  const src = name.trim() || email.trim()
  if (!src) return ''
  const parts = src.split(/\s+/).filter(Boolean)
  const letters =
    parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : src.slice(0, 2)
  return letters.toUpperCase()
}

/** The design's 15px / 500 bar-nav label, scaled by the brand base font size
 *  (navigation text follows the configured content size — Isobel, 2026-10-09):
 *  the design px at the 14px reference base, multiplied by the theme's own
 *  typography coefficient (MUI scales every variant by fontSize/14 — nav
 *  comes along). At the default base the pixels are unchanged. */
const navScaledPx = (t: Theme, designPx: number) => designPx * (t.typography.fontSize / 14)

const barNavLabelSx = {
  fontSize: (t: Theme) => navScaledPx(t, 15),
  fontWeight: 500,
}

/** The utility cluster's items (Isobel's 2026-10-11 spec): a 44px-centred
 *  touch target, padding inside the box, content centred both axes. Rides
 *  Appearance, Mailbox, the identity chip, Sign out and the hamburger. */
const clusterItemSx = {
  minHeight: 44,
  minWidth: 44,
  px: 1,
  py: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const

/** The cluster's thin vertical separators (Isobel's 2026-10-11 spec): only
 *  at the Mailbox↔Identity and Identity↔Sign out junctions, centred on the
 *  bar's cross axis, a touch taller on desktop. */
const clusterDividerSx = {
  height: { xs: 28, md: 32 },
  mx: { xs: 0.5, md: 1 },
  alignSelf: 'center',
  borderColor: 'divider',
} as const

/** The active destination's underline: 3px, rounded ends, tucked beneath the
 *  LABEL (not the whole button). Also the Explore button's open state. */
function navUnderlineSx(active: boolean) {
  return {
    position: 'relative',
    ...(active
      ? {
          '&::after': {
            content: '""', position: 'absolute', left: 0, right: 0, bottom: -3,
            height: 3, borderRadius: 2, bgcolor: 'primary.main',
          },
        }
      : {}),
  } as const
}

/** A row-trailing chevron in the accordion language. */
function rowChevron(expanded: boolean): ReactNode {
  return expanded
    ? <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
    : <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
}

/** An accordion region's DOM id from its expansion key. */
function regionId(prefix: string, key: string): string {
  return `${prefix}-${key.replace(/[^a-z0-9_-]/gi, '_')}`
}

/** Metered surfaces (surface.json `hideWhenMenuEmpty` + a menuOp): the
 *  launcher row renders ONLY when the fetched flyout proves ≥1 item —
 *  fail-closed: an unfetched menu (undefined) counts as empty, and a failed
 *  fetch has already landed as []. */
function meteredOut(s: LvSurfaceDef, menus: Record<string, SurfaceMenuItem[]> | undefined): boolean {
  return !!s.hideWhenMenuEmpty && !!s.menuOp && (menus?.[s.id] ?? []).length === 0
}

/** The drawer's Home-site-nav rows — the same accordion contract the desktop
 *  panel keeps through the canonical renderer (interactive folder rows, the
 *  current page's pill, ancestors marked), rendered as list buttons for the
 *  drawer's <nav> semantics, with the publish date riding as secondary text
 *  (Douglas 2026-09-20) and the drawer's roomier 36px guide line. */
function drawerNavItems(
  items: HomeNavLink[],
  currentFile: string | undefined,
  onSelect: (n: HomeNavLink, active: boolean) => void,
  accordion: NavAccordionState,
  depth = 0,
  parentKey = '',
): ReactNode[] {
  return items.flatMap((n) => {
    const key = navAccordionKey(n, parentKey)
    if (n.children?.length) {
      const isExpanded = accordion.expanded.has(key)
      const active = !!n.file && n.file === currentFile
      const ancestor = navAccordionContainsCurrent(n.children, currentFile)
      return [
        <ListItemButton
          key={`f-${key}`}
          selected={active}
          aria-expanded={isExpanded}
          aria-controls={regionId('dr', key)}
          sx={[navAccordionRowSx, navAccordionSelectedSx, { minHeight: 44 },
            ...(ancestor && !active ? [navAccordionAncestorSx] : [])]}
          onClick={() => (n.file ? onSelect(n, active) : accordion.onToggle(key))}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' && !isExpanded) { e.stopPropagation(); accordion.onToggle(key) }
            if (e.key === 'ArrowLeft' && isExpanded) { e.stopPropagation(); accordion.onToggle(key) }
          }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
            {isExpanded
              ? <FolderOpenIcon sx={{ fontSize: 18 }} />
              : <FolderIcon sx={{ fontSize: 18 }} />}
          </ListItemIcon>
          <ListItemText primary={n.label} secondary={n.date}
            primaryTypographyProps={{ className: 'nav-accordion-label' }} />
          {n.file ? (
            <Box
              component="span"
              role="button"
              aria-label={isExpanded ? `Collapse ${n.label}` : `Expand ${n.label}`}
              onClick={(e) => { e.stopPropagation(); accordion.onToggle(key) }}
              sx={{ display: 'inline-flex', p: 0.5, borderRadius: 1 }}
            >
              {rowChevron(isExpanded)}
            </Box>
          ) : rowChevron(isExpanded)}
        </ListItemButton>,
        <Collapse key={`c-${key}`} in={isExpanded} timeout="auto" unmountOnExit>
          <Box id={regionId('dr', key)} sx={navAccordionGuideSx(36)}>
            {drawerNavItems(n.children!, currentFile, onSelect, accordion, depth + 1, key)}
          </Box>
        </Collapse>,
      ]
    }
    const active = !!n.file && n.file === currentFile
    return [
      <ListItemButton
        key={n.file ?? `${n.href}|${n.label}`}
        selected={active}
        sx={[navAccordionRowSx, navAccordionSelectedSx, { minHeight: 44 }]}
        onClick={() => onSelect(n, active)}
      >
        <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
          <DescriptionIcon sx={{ fontSize: 18 }} />
        </ListItemIcon>
        <ListItemText primary={n.label} secondary={n.date}
          primaryTypographyProps={{ className: 'nav-accordion-label' }} />
      </ListItemButton>,
    ]
  })
}

export default function LvHeader({
  cfg,
  homeLabel,
  homeActive,
  homeLocked,
  onHome,
  explore,
  account,
  inbox,
  appearance,
}: {
  cfg: LvClientConfig | null
  homeLabel: string
  homeActive: boolean
  /** Public state: Home itself is a locked destination → sign-in flow. */
  homeLocked: boolean
  onHome: () => void
  explore: LvHeaderExplore
  account: LvHeaderAccount
  /** Signed-in only: the mailbox shortcut. Absent = no icon. */
  inbox?: LvHeaderInbox
  appearance: LvHeaderAppearance
}) {
  const [exploreAnchor, setExploreAnchor] = useState<HTMLElement | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  /** The accordion expansion sets: canonical homeNav keys plus 's-<id>' /
   *  'f-<id>' / 'explore' for the LV rows. Seeded with the current page's
   *  ancestors every time the panel or drawer opens (the design's
   *  auto-expand rule). */
  const [exploreExpanded, setExploreExpanded] = useState<ReadonlySet<string>>(new Set())
  const [drawerExpanded, setDrawerExpanded] = useState<ReadonlySet<string>>(new Set())
  const exploreOpen = !!exploreAnchor

  // The identity chip's photo: the learner's own classic profile image,
  // bearer-fetched once per tab (module-cached); null → initials render.
  // account.avatarUrl is the review/fixture override that skips the fetch.
  const [photo, setPhoto] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    if (account.kind === 'learner' && account.avatarUrl === undefined) {
      void selfPhotoUrl().then((u) => { if (live) setPhoto(u) })
    }
    return () => { live = false }
  }, [account.kind, account.avatarUrl])

  const toggleIn = (set: ReadonlySet<string>, apply: (next: ReadonlySet<string>) => void) =>
    (key: string) => {
      const next = new Set(set)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      apply(next)
    }
  const exploreAccordion: NavAccordionState = {
    expanded: exploreExpanded,
    onToggle: toggleIn(exploreExpanded, setExploreExpanded),
  }
  const drawerAccordion: NavAccordionState = {
    expanded: drawerExpanded,
    onToggle: toggleIn(drawerExpanded, setDrawerExpanded),
  }

  // ── Content-driven collapse (the design's responsive rule) ──────────────
  // The bar measures its own content against its own width — no device
  // breakpoints. Collapse hides Home/Explore behind the hamburger and drops
  // the identity name to initials; expanding requires the full content to
  // fit again with 48px of hysteresis so the state can't oscillate.
  const rootRef = useRef<HTMLDivElement>(null)
  const leftRef = useRef<HTMLDivElement>(null)
  const centreRef = useRef<HTMLDivElement>(null)
  const rightRef = useRef<HTMLDivElement>(null)
  const fullNeedRef = useRef(0)
  const [collapsed, setCollapsed] = useState(false)
  useLayoutEffect(() => {
    const root = rootRef.current
    if (!root || typeof ResizeObserver === 'undefined') return
    const measure = () => {
      const available = root.clientWidth - 32 // the bar's horizontal padding
      if (available <= 0) return
      if (!collapsed) {
        const need =
          (leftRef.current?.scrollWidth ?? 0) +
          (centreRef.current?.scrollWidth ?? 0) +
          (rightRef.current?.scrollWidth ?? 0) + 16
        if (need > available) {
          fullNeedRef.current = need
          setCollapsed(true)
          // Menus anchored to controls the collapse hides must not strand.
          setExploreAnchor(null)
        }
      } else if (fullNeedRef.current && available >= fullNeedRef.current + 48) {
        setCollapsed(false)
      }
    }
    const ro = new ResizeObserver(measure)
    ro.observe(root)
    if (leftRef.current) ro.observe(leftRef.current)
    if (centreRef.current) ro.observe(centreRef.current)
    if (rightRef.current) ro.observe(rightRef.current)
    measure()
    return () => ro.disconnect()
  }, [collapsed, explore.surfaces.length, explore.homeNav.length, account.kind,
      account.name, account.email, inbox?.unread, cfg])

  // The Explore panel's surfaces section: the resolved openable surfaces plus
  // the config's NAV FOLDERS, interleaved by config.navOrder when the operator
  // supplied one (listed ids first — known + enabled only — then unlisted
  // surfaces in resolved order and unlisted folders by label). No navOrder ⇒
  // surfaces in resolved order, then folders by label. Folders with zero
  // children never render (no stranded empty menus — the config route already
  // skips them; the filter is the hand-forged-config guard).
  const exploreRows: ({ kind: 'surface'; surface: LvSurfaceDef } | { kind: 'folder'; folder: LvFolderEntry })[] = []
  {
    const cfgFolders = (cfg?.folders ?? []).filter(
      (f) => f && typeof f.id === 'string' && Array.isArray(f.children) && f.children.length > 0,
    )
    const surfaceById = new Map(explore.surfaces.map((s) => [s.id, s]))
    const folderById = new Map(cfgFolders.map((f) => [f.id, f]))
    const listedS = new Set<string>()
    const listedF = new Set<string>()
    for (const id of cfg?.navOrder ?? []) {
      if (typeof id !== 'string') continue
      const s = surfaceById.get(id)
      // MENULESS surfaces (menu:false) never list, even when an operator
      // hand-places the id in navOrder — they open via openSurfaceTab only.
      if (s && s.menu !== false && !listedS.has(id)) { listedS.add(id); exploreRows.push({ kind: 'surface', surface: s }); continue }
      const f = folderById.get(id)
      if (f && !listedF.has(f.id)) { listedF.add(id); exploreRows.push({ kind: 'folder', folder: f }) }
    }
    for (const s of explore.surfaces) if (s.menu !== false && !listedS.has(s.id)) exploreRows.push({ kind: 'surface', surface: s })
    const restFolders = cfgFolders.filter((f) => !listedF.has(f.id)).sort((a, b) => a.label.localeCompare(b.label))
    for (const f of restFolders) exploreRows.push({ kind: 'folder', folder: f })
  }

  // ── The right-hand cluster, in the design's order ────────────────────────
  const appearanceToggle = appearance.allowed && (
    <Tooltip title={appearance.mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
      <IconButton
        color="inherit"
        aria-label={appearance.mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        onClick={() => appearance.onChoose(appearance.mode === 'light' ? 'dark' : 'light')}
        sx={clusterItemSx}
      >
        {appearance.mode === 'light'
          ? <LightModeIcon sx={{ fontSize: 20 }} />
          : <DarkModeIcon sx={{ fontSize: 20 }} />}
      </IconButton>
    </Tooltip>
  )

  const mailbox = inbox && account.kind !== 'public' && (
    <Tooltip title={inbox.unread > 0 ? `Mailbox, ${inbox.unread} unread` : 'Mailbox'}>
      <IconButton
        color="inherit"
        aria-label={inbox.unread > 0 ? `Mailbox, ${inbox.unread} unread` : 'Mailbox'}
        onClick={inbox.onOpen}
        sx={clusterItemSx}
      >
        <Badge badgeContent={inbox.unread} color="primary" max={99}>
          <MailOutlineIcon sx={{ fontSize: 20 }} />
        </Badge>
      </IconButton>
    </Tooltip>
  )

  const avatarSrc = account.kind === 'learner' ? account.avatarUrl ?? photo ?? undefined : undefined

  const identity = account.kind === 'public' ? (
    <Button
      size="small" variant="contained"
      startIcon={account.signingIn ? <CircularProgress size={14} color="inherit" /> : <LoginIcon sx={{ fontSize: 16 }} />}
      disabled={!!account.signingIn}
      onClick={account.onSignIn}
      sx={{ textTransform: 'none', ml: 0.5 }}
    >
      {account.signingIn ? 'Redirecting…' : 'Sign in'}
    </Button>
  ) : (
    /* The identity chip is DISPLAY-ONLY (Isobel 2026-10-09): the account
       dropdown is retired — its entries duplicated Explore's surface rows,
       and Sign out keeps its own bar icon. The avatar shows the classic
       profile photo when one exists, initials as the fallback; the name is
       the desktop affordance the collapse drops at the same moment
       Home/Explore fold away (content-measured, never a viewport
       breakpoint). */
    <Box sx={{ ...clusterItemSx, gap: 1, ...barNavLabelSx }}>
      <Avatar
        src={avatarSrc} alt=""
        sx={{ width: 34, height: 34, fontSize: 14, fontWeight: 700, bgcolor: avatarSrc ? 'transparent' : 'primary.main' }}
      >
        {initials(String(account.name ?? ''), String(account.email ?? '')) || <AccountCircleIcon sx={{ fontSize: 20 }} />}
      </Avatar>
      <Box
        component="span"
        sx={{ display: collapsed ? 'none' : 'inline', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {account.name || account.email || 'Signed in'}
      </Box>
    </Box>
  )

  const signOutButton = account.kind === 'learner' && account.onSignOut && (
    <Tooltip title="Sign out">
      <IconButton color="inherit" aria-label="Sign out" onClick={account.onSignOut} sx={clusterItemSx}>
        <LogoutIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>
  )

  /** The shared row renderer for one LV surface row's expansion items — the
   *  desktop panel and the drawer paint from the same fragments. */
  const surfaceChildren = (items: SurfaceMenuItem[], onPick: (it: SurfaceMenuItem) => void): ReactNode =>
    items.map((it) => (
      <MenuItem key={it.id} sx={[navAccordionRowSx, navAccordionSelectedSx]} onClick={() => onPick(it)}>
        <Typography variant="body2" className="nav-accordion-label" sx={{ flexGrow: 1, pl: 4.5 }}>
          {it.label}
        </Typography>
        {/* Two-sided surfaces: the item offers more than one face (e.g. a pool
            you lead) — the same glyph the tab's flip button carries, so the
            affordance reads the same here. */}
        {it.faces && it.faces.length > 1 && (
          <FlipIcon sx={{ fontSize: 13, ml: 1, color: 'text.secondary' }} />
        )}
      </MenuItem>
    ))

  /** The placeholder copy for a menuOp row expanded before its flyout fetch
   *  has resolved (undefined = in flight) or after it landed empty/failed —
   *  the fetch collapses failure to zero items by contract, so one line
   *  covers both. */
  const subpagesPlaceholder = (items: SurfaceMenuItem[] | undefined): string =>
    items === undefined ? 'Loading…' : 'No subpages available'

  return (
    <Stack
      ref={rootRef}
      direction="row" alignItems="center" component="header"
      sx={{
        // 64px while the full navigation fits, 56px collapsed — the design's
        // targets, driven by the same content measurement as the collapse.
        height: collapsed ? 56 : 64, flexShrink: 0, px: 2, boxSizing: 'border-box',
        borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper',
        color: 'text.primary',
      }}
    >
      {/* LEFT: the customer's mark (or the deployment's name as text). */}
      <Box ref={leftRef} sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <LvLogo cfg={cfg} mode={appearance.mode} />
      </Box>

      {/* CENTRE: Home and Explore, centred in the space between the logo
          region and the utility controls (Isobel, 2026-10-11). The inner nav
          shrink-wraps its content, so the collapse measurement reads the
          content's own width, never the flex-grown region's. */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
        <Stack
          ref={centreRef}
          direction="row" alignItems="center" spacing={1}
          component="nav" aria-label="Learner view"
        >
        {!collapsed && (
          <>
            <Button
              color="inherit" onClick={onHome}
              aria-current={!homeLocked && homeActive ? 'page' : undefined}
              aria-label={homeLocked ? `${homeLabel} (requires sign in)` : undefined}
              startIcon={homeLocked ? <LockIcon sx={{ fontSize: 14 }} /> : undefined}
              sx={{
                textTransform: 'none',
                ...barNavLabelSx,
                fontWeight: !homeLocked && homeActive ? 600 : 500,
                color: homeLocked ? 'text.secondary' : undefined,
              }}
            >
              <Box component="span" sx={navUnderlineSx(!homeLocked && homeActive)}>
                {homeLabel}
              </Box>
            </Button>

            <Button
              color="inherit" endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
              aria-haspopup="menu" aria-expanded={exploreOpen ? 'true' : undefined}
              aria-controls={exploreOpen ? 'lv-explore-menu' : undefined}
              onClick={(e) => {
                // The design's auto-expand: the panel opens with the current
                // page's ancestors already expanded.
                setExploreExpanded(new Set(navAccordionInitialKeys(explore.homeNav, explore.currentFile)))
                setExploreAnchor(e.currentTarget)
              }}
              sx={{ textTransform: 'none', ...barNavLabelSx }}
            >
              <Box component="span" sx={navUnderlineSx(exploreOpen)}>
                Explore
              </Box>
            </Button>
            <Menu
              id="lv-explore-menu" anchorEl={exploreAnchor} open={exploreOpen}
              onClose={() => setExploreAnchor(null)}
              MenuListProps={{ 'aria-label': 'Explore' }}
              PaperProps={{
                sx: {
                  minWidth: 320,
                  // The panel widens with the base font size so a long row
                  // (label + trailing date) keeps its single-line rhythm
                  // instead of clipping at the 400px design width.
                  maxWidth: (t: Theme) => navScaledPx(t, 400),
                  borderRadius: (t) => `${t.shape.borderRadius * 1.5}px`,
                },
              }}
            >
              {/* The panel title, per the approved mock — scaled like every
                  other nav label (15.2px / 34px line at the 14px base). */}
              <ListSubheader
                disableSticky
                sx={{
                  fontWeight: 700,
                  fontSize: (t: Theme) => navScaledPx(t, 15.2),
                  lineHeight: (t: Theme) => `${navScaledPx(t, 34)}px`,
                  color: 'text.primary', userSelect: 'none',
                }}
              >
                Explore
              </ListSubheader>

              {/* Home — the design's first row, its existing destination. */}
              <MenuItem
                selected={!homeLocked && homeActive}
                aria-current={!homeLocked && homeActive ? 'page' : undefined}
                aria-label={homeLocked ? `${homeLabel} (requires sign in)` : undefined}
                sx={[navAccordionRowSx, navAccordionSelectedSx]}
                onClick={() => {
                  setExploreAnchor(null)
                  if (homeLocked) explore.onLocked()
                  else onHome()
                }}
              >
                <ListItemIcon sx={navAccordionIconSx}><HomeIcon /></ListItemIcon>
                <Typography variant="body2" className="nav-accordion-label" sx={{ flexGrow: 1 }}>
                  {homeLabel}
                </Typography>
                {homeLocked && <LockIcon sx={{ fontSize: 14, ml: 1, color: 'text.secondary' }} />}
              </MenuItem>

              {/* Home's site menu — the CANONICAL accordion renderer, so this
                  panel and Home's own navigation can never drift. A corpus row
                  that re-points at the corpus root with the Home entry's own
                  label is dropped here — the shell's Home entry above already
                  covers it (the every-deployment Home×2 duplicate). */}
              {renderNavAccordionItems(
                explore.homeNav.filter((n) => {
                  // The shell's Home entry above covers every leaf row that
                  // just re-points home with its label (corpus-root file OR
                  // an href-shaped home row, e.g. the template corpus's).
                  const sameLabel = (n.label ?? '').trim().toLowerCase() === (homeLabel ?? 'Home').trim().toLowerCase()
                  if (!sameLabel) return true
                  const root = !n.file || n.file === 'index' || n.file === 'index.md' || n.file.endsWith('/index.md')
                  const leaf = !n.children || n.children.length === 0
                  return !(root && leaf)
                }),
                explore.currentFile,
                (n, active) => {
                  setExploreAnchor(null)
                  if (!active) explore.onHomeNav(n)
                },
                exploreAccordion,
              )}

              {explore.homeNav.length > 0 && exploreRows.length > 0 && <Divider sx={{ my: 0.5 }} />}

              {exploreRows.map((row) => {
                if (row.kind === 'folder') {
                  // A nav FOLDER groups pages in the panel — it never opens a
                  // tab; its row expands inline to its member pages.
                  const f = row.folder
                  const key = `f-${f.id}`
                  const isExpanded = exploreExpanded.has(key)
                  return (
                    <Fragment key={key}>
                      <MenuItem
                        aria-label={explore.locked ? `${f.label} (requires sign in)` : undefined}
                        aria-expanded={explore.locked ? undefined : isExpanded}
                        aria-controls={explore.locked ? undefined : regionId('ex', key)}
                        sx={[navAccordionRowSx, navAccordionSelectedSx]}
                        onClick={() => {
                          if (explore.locked) {
                            setExploreAnchor(null)
                            explore.onLocked()
                          } else {
                            exploreAccordion.onToggle(key)
                          }
                        }}
                      >
                        <ListItemIcon sx={navAccordionIconSx}><SurfaceGlyph icon={f.icon ?? 'Folder'} size={16} /></ListItemIcon>
                        <Typography variant="body2" className="nav-accordion-label" sx={{ flexGrow: 1 }}>
                          {f.label}
                        </Typography>
                        {explore.locked ? (
                          <LockIcon sx={{ fontSize: 14, ml: 1, color: 'text.secondary' }} />
                        ) : rowChevron(isExpanded)}
                      </MenuItem>
                      <Collapse in={!explore.locked && isExpanded} timeout="auto" unmountOnExit>
                        <Box id={regionId('ex', key)} sx={navAccordionGuideSx()}>
                          {f.children.map((c) => (
                            <MenuItem
                              key={c.id}
                              sx={[navAccordionRowSx, navAccordionSelectedSx]}
                              onClick={() => { setExploreAnchor(null); explore.onSurface(c.id) }}
                            >
                              <ListItemIcon sx={navAccordionIconSx}><SurfaceGlyph icon={c.icon ?? 'Description'} size={16} /></ListItemIcon>
                              <Typography variant="body2" className="nav-accordion-label" sx={{ flexGrow: 1 }}>
                                {c.label}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Box>
                      </Collapse>
                    </Fragment>
                  )
                }
                const s = row.surface
                // Metered surfaces (hideWhenMenuEmpty): NO launcher row until
                // the menu op proves ≥1 item — fail-closed while unfetched.
                if (meteredOut(s, explore.menus)) return null
                // Expandability rides the manifest's menuOp declaration —
                // never the asynchronously fetched items (Isobel 2026-10-09):
                // a menuOp surface's row is an accordion PARENT, so the whole
                // row expands its subpages and never navigates. Locked rows
                // still route to sign-in, an href row still opens externally,
                // and a plain surface's row still opens its surface.
                const expandable = !explore.locked && !s.href && !!s.menuOp
                const items = expandable ? explore.menus?.[s.id] ?? [] : []
                const key = `s-${s.id}`
                const isExpanded = exploreExpanded.has(key)
                return (
                  <Fragment key={key}>
                    <MenuItem
                      aria-label={explore.locked ? `${s.label} (requires sign in)` : undefined}
                      aria-expanded={expandable ? isExpanded : undefined}
                      aria-controls={expandable ? regionId('ex', key) : undefined}
                      sx={[navAccordionRowSx, navAccordionSelectedSx]}
                      onClick={() => {
                        if (explore.locked) {
                          setExploreAnchor(null)
                          explore.onLocked()
                        } else if (expandable) {
                          // Expand-only: the surface's own landing is not a
                          // menu destination — the subpages are.
                          exploreAccordion.onToggle(key)
                        } else {
                          setExploreAnchor(null)
                          explore.onSurface(s.id)
                        }
                      }}
                      onKeyDown={(e) => {
                        if (!expandable) return
                        if (e.key === 'ArrowRight' && !isExpanded) { e.stopPropagation(); exploreAccordion.onToggle(key) }
                        if (e.key === 'ArrowLeft' && isExpanded) { e.stopPropagation(); exploreAccordion.onToggle(key) }
                      }}
                    >
                      <ListItemIcon sx={navAccordionIconSx}><SurfaceGlyph icon={s.icon} size={16} /></ListItemIcon>
                      <Typography variant="body2" className="nav-accordion-label" sx={{ flexGrow: 1 }}>
                        {s.label}
                      </Typography>
                      {explore.locked ? (
                        <LockIcon sx={{ fontSize: 14, ml: 1, color: 'text.secondary' }} />
                      ) : s.href ? (
                        <OpenInNewIcon sx={{ fontSize: 13, ml: 1, color: 'text.secondary' }} />
                      ) : expandable ? (
                        rowChevron(isExpanded)
                      ) : null}
                    </MenuItem>
                    {expandable && (
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box id={regionId('ex', key)} sx={navAccordionGuideSx()}>
                          {items.length > 0 ? (
                            surfaceChildren(items, (it) => {
                              setExploreAnchor(null)
                              explore.onSurface(s.id, it.label, { menu: it.id })
                            })
                          ) : (
                            // A declared-but-empty (or failed) flyout keeps
                            // its expandable row — the placeholder says so
                            // instead of stranding a dead expander.
                            <MenuItem disabled sx={navAccordionRowSx}>
                              <Typography variant="body2" className="nav-accordion-label" sx={{ flexGrow: 1, pl: 4.5, color: 'text.secondary' }}>
                                {subpagesPlaceholder(explore.menus?.[s.id])}
                              </Typography>
                            </MenuItem>
                          )}
                        </Box>
                      </Collapse>
                    )}
                  </Fragment>
                )
              })}
              {explore.homeNav.length === 0 && exploreRows.length === 0 && (
                <MenuItem disabled>
                  <ListItemText secondary="No destinations are available on this deployment." />
                </MenuItem>
              )}
            </Menu>
          </>
        )}
        </Stack>
      </Box>

      {/* RIGHT: Appearance · Mailbox · Identity · Sign out · [☰] — the thin
          vertical separator rides at ONLY the Mailbox↔Identity and
          Identity↔Sign out junctions (Isobel, 2026-10-11), centred on the
          cross axis; items are the spec's 44px-centred targets. */}
      <Stack
        ref={rightRef}
        direction="row" alignItems="center"
        sx={{ flexShrink: 0 }}
      >
        {appearanceToggle}
        {mailbox}
        {mailbox && <Divider orientation="vertical" sx={clusterDividerSx} />}
        {identity}
        {signOutButton && <Divider orientation="vertical" sx={clusterDividerSx} />}
        {signOutButton}

        {collapsed && (
          <Tooltip title="Open navigation">
            <IconButton
              color="inherit"
              aria-label="Open navigation"
              aria-haspopup="true"
              aria-expanded={drawerOpen || undefined}
              onClick={() => {
                setDrawerExpanded(new Set(navAccordionInitialKeys(explore.homeNav, explore.currentFile)))
                setDrawerOpen(true)
              }}
              sx={clusterItemSx}
            >
              <MenuIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* The collapsed navigation drawer — the SAME destinations and callbacks
          as the desktop Home/Explore controls in the same accordion language:
          Home, Home's site nav behind Explore, and the Surfaces section with
          the same rows, expansions and nav folders. No second navigation
          system, no new destinations. */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 320, maxWidth: '85vw' } }}
      >
        <Stack
          direction="row" alignItems="center"
          sx={{ px: 2, height: 56, flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}
        >
          <LvLogo cfg={cfg} mode={appearance.mode} />
          <Box sx={{ flex: 1 }} />
          <IconButton aria-label="Close navigation" onClick={() => setDrawerOpen(false)}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
        <Box component="nav" aria-label="Learner view drawer" sx={{ overflowY: 'auto', py: 0.5 }}>
          <List disablePadding>
            <ListItemButton
              selected={!homeLocked && homeActive}
              aria-current={!homeLocked && homeActive ? 'page' : undefined}
              aria-label={homeLocked ? `${homeLabel} (requires sign in)` : undefined}
              sx={[navAccordionRowSx, navAccordionSelectedSx, { minHeight: 44 }]}
              onClick={() => { setDrawerOpen(false); onHome() }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}><HomeIcon sx={{ fontSize: 18 }} /></ListItemIcon>
              <ListItemText primary={homeLabel} primaryTypographyProps={{ className: 'nav-accordion-label' }} />
              {homeLocked && <LockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
            </ListItemButton>

            {explore.homeNav.length > 0 && (
              <>
                <ListItemButton
                  aria-expanded={drawerExpanded.has('explore')}
                  aria-controls="dr-explore"
                  sx={[navAccordionRowSx, { minHeight: 44 }]}
                  onClick={() => drawerAccordion.onToggle('explore')}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}><MenuBookIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                  <ListItemText primary="Explore" primaryTypographyProps={{ className: 'nav-accordion-label' }} />
                  {rowChevron(drawerExpanded.has('explore'))}
                </ListItemButton>
                <Collapse in={drawerExpanded.has('explore')} timeout="auto" unmountOnExit>
                  <Box id="dr-explore" sx={navAccordionGuideSx(36)}>
                    {drawerNavItems(explore.homeNav, explore.currentFile, (n, active) => {
                      setDrawerOpen(false)
                      if (!active) explore.onHomeNav(n)
                    }, drawerAccordion)}
                  </Box>
                </Collapse>
              </>
            )}

            {exploreRows.length > 0 && (
              <ListSubheader
                disableSticky
                // The drawer's 12px section label, scaled like the bar labels.
                sx={{ fontWeight: 700, fontSize: (t: Theme) => navScaledPx(t, 12), color: 'text.secondary' }}
              >
                Surfaces
              </ListSubheader>
            )}
            {exploreRows.map((row) => {
              if (row.kind === 'folder') {
                const f = row.folder
                const key = `f-${f.id}`
                const expanded = drawerExpanded.has(key)
                return (
                  <Fragment key={key}>
                    <ListItemButton
                      aria-label={explore.locked ? `${f.label} (requires sign in)` : undefined}
                      aria-expanded={explore.locked ? undefined : expanded}
                      aria-controls={explore.locked ? undefined : regionId('dr', key)}
                      sx={[navAccordionRowSx, navAccordionSelectedSx, { minHeight: 44 }]}
                      onClick={() => {
                        if (explore.locked) { setDrawerOpen(false); explore.onLocked() }
                        else drawerAccordion.onToggle(key)
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}><SurfaceGlyph icon={f.icon ?? 'Folder'} size={18} /></ListItemIcon>
                      <ListItemText primary={f.label} primaryTypographyProps={{ className: 'nav-accordion-label' }} />
                      {explore.locked ? (
                        <LockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      ) : rowChevron(expanded)}
                    </ListItemButton>
                    <Collapse in={!explore.locked && expanded} timeout="auto" unmountOnExit>
                      <Box id={regionId('dr', key)} sx={navAccordionGuideSx(36)}>
                        {f.children.map((c) => (
                          <ListItemButton
                            key={c.id}
                            sx={[navAccordionRowSx, navAccordionSelectedSx, { minHeight: 44 }]}
                            onClick={() => { setDrawerOpen(false); explore.onSurface(c.id) }}
                          >
                            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}><SurfaceGlyph icon={c.icon ?? 'Description'} size={18} /></ListItemIcon>
                            <ListItemText primary={c.label} primaryTypographyProps={{ className: 'nav-accordion-label' }} />
                          </ListItemButton>
                        ))}
                      </Box>
                    </Collapse>
                  </Fragment>
                )
              }
              const s = row.surface
              const key = `s-${s.id}`
              const expanded = drawerExpanded.has(key)
              // Metered surfaces (hideWhenMenuEmpty): NO drawer row until the
              // menu op proves ≥1 item — the same fail-closed meter as the
              // desktop panel.
              if (meteredOut(s, explore.menus)) return null
              // The same manifest-keyed expand-only rule as the desktop panel:
              // menuOp ⇒ an accordion parent, never a navigation row.
              const expandable = !explore.locked && !s.href && !!s.menuOp
              const items = expandable ? explore.menus?.[s.id] ?? [] : []
              return (
                <Fragment key={key}>
                  <ListItemButton
                    aria-label={explore.locked ? `${s.label} (requires sign in)` : undefined}
                    aria-expanded={expandable ? expanded : undefined}
                    aria-controls={expandable ? regionId('dr', key) : undefined}
                    sx={[navAccordionRowSx, navAccordionSelectedSx, { minHeight: 44 }]}
                    onClick={() => {
                      if (explore.locked) { setDrawerOpen(false); explore.onLocked() }
                      else if (expandable) drawerAccordion.onToggle(key)
                      else { setDrawerOpen(false); explore.onSurface(s.id) }
                    }}
                    onKeyDown={(e) => {
                      if (!expandable) return
                      if (e.key === 'ArrowRight' && !expanded) { e.stopPropagation(); drawerAccordion.onToggle(key) }
                      if (e.key === 'ArrowLeft' && expanded) { e.stopPropagation(); drawerAccordion.onToggle(key) }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}><SurfaceGlyph icon={s.icon} size={18} /></ListItemIcon>
                    <ListItemText primary={s.label} primaryTypographyProps={{ className: 'nav-accordion-label' }} />
                    {explore.locked ? (
                      <LockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    ) : s.href ? (
                      <OpenInNewIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                    ) : expandable ? (
                      rowChevron(expanded)
                    ) : null}
                  </ListItemButton>
                  {expandable && (
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                      <Box id={regionId('dr', key)} sx={navAccordionGuideSx(36)}>
                        {items.length > 0 ? (
                          items.map((it) => (
                            <ListItemButton
                              key={it.id}
                              sx={[navAccordionRowSx, navAccordionSelectedSx, { minHeight: 44 }]}
                              onClick={() => { setDrawerOpen(false); explore.onSurface(s.id, it.label, { menu: it.id }) }}
                            >
                              <ListItemText primary={it.label} sx={{ pl: 4.5 }}
                                primaryTypographyProps={{ className: 'nav-accordion-label' }} />
                              {it.faces && it.faces.length > 1 && (
                                <FlipIcon sx={{ fontSize: 13, ml: 1, color: 'text.secondary' }} />
                              )}
                            </ListItemButton>
                          ))
                        ) : (
                          <ListItemButton disabled sx={[navAccordionRowSx, { minHeight: 44 }]}>
                            <ListItemText primary={subpagesPlaceholder(explore.menus?.[s.id])} sx={{ pl: 4.5 }}
                              primaryTypographyProps={{ className: 'nav-accordion-label' }} />
                          </ListItemButton>
                        )}
                      </Box>
                    </Collapse>
                  )}
                </Fragment>
              )
            })}
            {explore.homeNav.length === 0 && exploreRows.length === 0 && (
              <ListItemText
                secondary="No destinations are available on this deployment."
                sx={{ px: 2, py: 1 }}
              />
            )}
          </List>
        </Box>
      </Drawer>
    </Stack>
  )
}
